import React, { useMemo, useRef } from 'react';
import {
	Autocomplete,
	TextField,
	AutocompleteProps,
	styled,
	Popper,
} from '@mui/material';

type Props<T> = AutocompleteProps<T, false, false, false> & {
	label?: string;
	placeholder?: string;
};

function getTextWidth(text: string, font = '14px sans-serif') {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	if (!context) return 0;

	context.font = font;
	return context.measureText(text).width;
}

export default function AppAutocomplete<T>({
	label,
	placeholder,
	renderInput,
	renderOption,
	options,
	getOptionLabel,
	...props
}: Props<T>) {
	const rootRef = useRef<HTMLDivElement | null>(null);

	const popperWidth = useMemo(() => {
		if (!options?.length) return 0;

		const getLabel = getOptionLabel || ((option: T) => String(option));

		const longest = options.reduce((a, b) =>
			getLabel(a).length > getLabel(b).length ? a : b
		);

		const inputWidth = rootRef.current?.offsetWidth || 0;

		const input = document.querySelector(
			'.MuiAutocomplete-input'
		) as HTMLElement;

		const computedFont = input
			? window.getComputedStyle(input).font
			: '14px speeday';

		const textWidth = getTextWidth(getLabel(longest), computedFont);

		const padding = 50;

		return Math.max(inputWidth, textWidth + padding);
	}, [options, rootRef]);

	const ContentSizedPopper = styled(Popper)({
		...(popperWidth ? { width: `${popperWidth}px !important` } : {}),
	});

	return (
		<Autocomplete
			{...props}
			ref={rootRef}
			options={options}
			slots={{
				popper: ContentSizedPopper,
			}}
			renderOption={renderOption || undefined}
			renderInput={(params) =>
				renderInput ? (
					renderInput(params)
				) : (
					<TextField
						{...params}
						label={label}
						placeholder={placeholder}
						fullWidth
						sx={{
							'& .MuiInputBase-input': {
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							},
						}}
					/>
				)
			}
			sx={{
				'& .MuiAutocomplete-input': {
					whiteSpace: 'normal',
				},
				'& .MuiAutocomplete-option': {
					whiteSpace: 'normal',
				},
			}}
		/>
	);
}
